import React, { useState, useEffect } from 'react';
import { MessageSquare, Info } from 'lucide-react';
import ConversationCard from './ConversationCard';
import SummaryDashboard from '../common/SummaryDashboard';
import LoadingState from '../common/LoadingState';
import { buildApiUrl } from '../../../../services/apiConfig';

const NextBestConversationsTab = ({ clientBehavioralData, selectedClient }) => {
  const [expandedTopic, setExpandedTopic] = useState(null);
  const [conversationTopics, setConversationTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [triggers, setTriggers] = useState([]);

  const toggleTopic = (id) => {
    if (expandedTopic === id) {
      setExpandedTopic(null);
    } else {
      setExpandedTopic(id);
    }
  };

  // Helper function to extract JSON from markdown code blocks
  const extractJsonFromMarkdown = (text) => {
    if (text.includes('```json')) {
      const match = text.match(/```json\s*([\s\S]*?)\s*```/);
      if (match && match[1]) {
        return match[1].trim();
      }
    }
    return text;
  };

  // First, get the triggers from sessionStorage
  const fetchTriggers = () => {
    if (!selectedClient) return [];

    // Use the same page load key as in TriggerNotificationsTab
    const pageLoadKey = sessionStorage.getItem('page_load_key');

    // Check if we have cached triggers for this client and this page load
    const cachedTriggersKey = `triggers_${selectedClient.id}_${pageLoadKey}`;
    const cachedTriggers = sessionStorage.getItem(cachedTriggersKey);

    if (cachedTriggers) {
      return JSON.parse(cachedTriggers);
    }

    return [];
  };

  const fallbackConversation = {
    id: "conv_fallback",
    title: "Portfolio Review and Goal Alignment",
    type: "Portfolio Strategy",
    description: "Regular portfolio review to ensure alignment with client's financial goals and risk tolerance, especially given current market conditions.",
    priority: "Medium",
    triggerId: null,
    talkingPoints: [
      "Review current portfolio performance against benchmarks",
      "Discuss any changes in financial goals or risk tolerance",
      "Explore opportunities for portfolio rebalancing or optimization"
    ],
    tone: "Informative",
    resources: [
      "Portfolio performance summary report",
      "Market outlook analysis"
    ],
    timing: "Next meeting",
    expectedResponse: "Interested"
  };

  const fetchConversations = async () => {
    if (!selectedClient) return;

    const pageLoadKey = sessionStorage.getItem('page_load_key');

    const cachedConversationsKey = `conversations_${selectedClient.id}_${pageLoadKey}`;
    const cachedConversations = sessionStorage.getItem(cachedConversationsKey);

    if (cachedConversations) {
      setConversationTopics(JSON.parse(cachedConversations));
      return;
    }

    setLoading(true);
    try {
      // Get triggers first
      const clientTriggers = fetchTriggers();
      setTriggers(clientTriggers);


      // Create a FormData object
      const formData = new FormData();

      // Create a JSON string for the client data with triggers
      const clientData = JSON.stringify({
        "client": {
          "id": selectedClient.id,
          "name": selectedClient.name,
          "age": selectedClient.age || 58,
          "occupation": selectedClient.occupation || "Retired",
          "annual_income": selectedClient.annual_income || 250000,
          "net_worth": selectedClient.net_worth || 12800000,
          "risk_profile": selectedClient.risk_profile || "Moderate",
          "investment_horizon": selectedClient.investment_horizon || "5-10 years",
          "portfolio": selectedClient.portfolio || {
            "total_value": 9100000,
            "allocation": {
              "equities": 65,
              "fixed_income": 20,
              "alternatives": 10,
              "cash": 5
            }
          },
          "goals": selectedClient.goals || [
            "Retirement income planning",
            "Estate planning",
            "Philanthropic initiatives"
          ]
        },
        "triggers": clientTriggers,
        "metadata": {
          "timestamp": new Date().toISOString()
        }
      });

      // Create a Blob from the JSON string
      const fileBlob = new Blob([clientData], { type: 'application/json' });

      // Append the file to the FormData
      formData.append('file', fileBlob, 'client-data.json');

      // Append the prompt to the FormData
      formData.append('prompt', promptText);

      // Make the API call
      const response = await fetch(buildApiUrl('JAMBA', '/custom-analyze'), {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }

      const result = await response.json();

      // Extract JSON from markdown if needed
      const cleanJsonString = extractJsonFromMarkdown(result.data);

      const parsedConversations = JSON.parse(cleanJsonString);

      // Save to state and sessionStorage
      setConversationTopics(parsedConversations);
      sessionStorage.setItem(cachedConversationsKey, JSON.stringify(parsedConversations));

    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError(err.message);
      console.warn('API call failed, using fallback conversation');
      setConversationTopics([fallbackConversation]);
      sessionStorage.setItem(cachedConversationsKey, JSON.stringify([fallbackConversation]));

    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Generate a unique page load key when the component mounts if it doesn't exist
    if (!sessionStorage.getItem('page_load_key')) {
      sessionStorage.setItem('page_load_key', Date.now().toString());
    }

    if (selectedClient) {
      fetchConversations();
    }
  }, [selectedClient]);

  const promptText = `Based on the provided client data and any detected triggers, generate EXACTLY 1 personalized conversation recommendation for a financial advisor.
  
For each trigger in the client data, create at least one corresponding conversation topic that addresses the underlying issue or opportunity.
If no triggers are provided, generate ONLY 1 general conversation topic based on the client's profile, goals, and portfolio.
  
Format the response as a JSON array with the following structure for each conversation topic:
[
  {
    "id": "conv_" + [random 8-character alphanumeric string],
    "title": "Brief descriptive title of the conversation topic",
    "type": "Portfolio Strategy | Retirement Planning | Estate Planning | Tax Strategy | Life Event | Market Update",
    "description": "Detailed description of what to discuss with the client and why it matters",
    "priority": "High | Medium | Low",
    "triggerId": [ID of the related trigger if applicable, otherwise null],
    "talkingPoints": [
      "Specific point the advisor should discuss with the client",
      "Another key discussion point",
      "A third discussion point if applicable"
    ],
    "tone": "Informative | Reassuring | Urgent | Exploratory | Congratulatory",
    "resources": [
      "Specific resource or document the advisor could share",
      "Another recommended resource if applicable"
    ],
    "timing": "Next meeting | Within 30 days | Quarterly review | Immediate",
    "expectedResponse": "Positive | Concerned | Neutral | Interested | Resistant"
  }
]
  
Priority guidelines:
- High: Immediate conversation needed, significant financial impact, time-sensitive
- Medium: Should be discussed in the next meeting, moderate financial impact
- Low: For awareness, can be addressed when convenient
  
Ensure each conversation topic includes:
1. Clear talking points that are specific and actionable
2. Appropriate tone recommendation based on the nature of the topic
3. Relevant resources the advisor could share with the client
4. Connection to a specific trigger when applicable
5. Expected timing for the conversation
6. Anticipated client response to the topic
  
DO NOT return any additional text.
If the client data doesn't provide enough information for meaningful recommendations, create general conversation topics based on the client's age, wealth level, and stated goals.
IMPORTANT: Return EXACTLY ONE conversation topic regardless of how many triggers might be present in the client data.
`;

  if (loading) {
    return <LoadingState message="Loading conversation recommendations..." />;
  }

  if (error && (!conversationTopics || conversationTopics.length === 0)) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <p className="text-red-500">Error loading conversation recommendations: {error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
      <div className="flex justify-between items-center mb-6">
        <p className="text-gray-600">
          Generates personalized conversation recommendations to enhance client interactions.
        </p>
        <button
          onClick={() => setShowPrompt(!showPrompt)}
          className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
          title="Show prompt information"
        >
          <Info size={18} className="text-gray-500" />
        </button>
      </div>

      {showPrompt && (
        <div className="bg-gray-50 p-4 rounded-md mb-6 border border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Analysis Prompt:</h4>
          <pre className="text-xs text-gray-600 whitespace-pre-wrap">{promptText}</pre>
        </div>
      )}

      <div className="bg-green-50 border border-green-100 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <div className="bg-green-100 rounded-full p-2 mr-4">
            <MessageSquare size={20} className="text-green-600" />
          </div>
          <div>
            <h3 className="font-semibold text-green-800 mb-1">Trigger-to-Conversation Topics</h3>
            <p className="text-sm text-green-700">
              Each detected trigger automatically generates personalized conversation recommendations with suggested talking points, appropriate tone, and relevant resources to guide your client interactions.
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center mb-4">
        <div className="flex space-x-2">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            {conversationTopics.filter(t => t.priority === 'High').length} High Priority
          </span>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            {conversationTopics.filter(t => t.priority === 'Medium').length} Medium Priority
          </span>
        </div>
      </div>

      {conversationTopics.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500">No conversation recommendations found for this client.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {conversationTopics.map((topic) => {
            const relatedTrigger = triggers.find(t => t.id === topic.triggerId);
            return (
              <ConversationCard
                key={topic.id}
                topic={topic}
                relatedTrigger={relatedTrigger}
                isExpanded={expandedTopic === topic.id}
                onToggle={toggleTopic}
              />
            );
          })}
        </div>
      )}

    </div>
  );
};

export default NextBestConversationsTab;